import { DataAmigavel } from '@/shared/utils/datas';
import { Document, Page, Text, View, } from '@react-pdf/renderer';
import { IFullReport, IReportLocalLotacao, IReportComarca } from '@/api.types';
import { stylesDefensorComarca } from './styles/defensores-comarca.style';
import { montarGruposDefensorias } from '@/modules/business/lotacoes/lotacoes-grouping.utils';

// Constants for pagination
const ALTURA_PAGINA = 600; // Estimated usable page height
const ALTURA_CABECALHO = 40; // Header height (comarca, atuação, prédio)
const ALTURA_TITULOS_TABELA = 24; // Table header height
const ALTURA_LINHA = 22; // Table row height

// Pagination function
function paginarLinhas<T>(linhas: T[]): T[][] {
  const paginas: T[][] = [];
  let paginaAtual: T[] = [];
  let alturaAcumulada = ALTURA_CABECALHO + ALTURA_TITULOS_TABELA;

  for (const linha of linhas) {
    if (alturaAcumulada + ALTURA_LINHA > ALTURA_PAGINA) {
      paginas.push(paginaAtual);
      paginaAtual = [];
      alturaAcumulada = ALTURA_CABECALHO + ALTURA_TITULOS_TABELA;
    }

    paginaAtual.push(linha);
    alturaAcumulada += ALTURA_LINHA;
  }

  if (paginaAtual.length > 0) {
    paginas.push(paginaAtual);
  }

  return paginas;
}

// Interface for component props
interface IProps {
  relatorio: IFullReport | null; // Permitir null
  exibirEstagiarios: boolean;
  exibirEventos: boolean;
  agruparAtuacao: boolean;
  agruparPorPredio: boolean;
  agruparPorDiretoria: boolean;
  agruparComarca: boolean;
  selectedComarcas: string[];
  selectedDefensorias: string[];
  selectedDefensores: string[];
}

// Helper function to check if a lotação is active
export function isAtivoHoje(data_inicial?: Date | string, data_final?: Date | string): boolean {
  const hoje = new Date();
  const inicio = data_inicial ? new Date(data_inicial) : null;
  const fim = data_final ? new Date(data_final) : null;
  return (
    (!inicio || inicio <= hoje) &&
    (!fim || fim >= hoje)
  );
}

// Helper function to format leave events
export function getEventos(relatorio: IFullReport, defensorId: number, idDefensoria: number): string {
  const defensor = relatorio.pessoas.find((p) => p.id === defensorId);
  if (!defensor?.abonos) return '';

  const eventos = defensor.abonos
    .filter((abono) =>
      abono.substitutos.some((s) => s.defensoria_id === idDefensoria),
    )
    .map((folga) => {
      const tipoMap: { [key: number]: string } = {
        1: 'Férias',
        2: 'Folga Natalícia',
        3: 'Abono Trabalhado',
        4: 'Abono Especial',
        5: 'Licença-Prêmio',
        6: 'Licença (Outros)',
        7: 'Afastamento',
      };
      const tipo = tipoMap[folga.tipo] || 'Folga';
      const ini = DataAmigavel(folga.data_inicial);
      const fim = DataAmigavel(folga.data_final);
      const dias = Math.floor(
        (new Date(folga.data_final).getTime() - new Date(folga.data_inicial).getTime()) /
        (1000 * 60 * 60 * 24)
      ) + 1;

      const substitutos = folga.substitutos
        .map((sub) => {
          const subst = relatorio.pessoas.find((p) => p.id === sub.defensor_id);
          return subst ? `${subst.titulo || ''} ${subst.nome}`.trim() : '';
        })
        .filter(Boolean)
        .join(', ');

      return `${tipo}: ${ini} a ${fim} (${dias} dias)${substitutos ? '. Substituto(s): ' + substitutos : ''}`;
    });

  return eventos.join('\n');
}

// Helper function to extract number from defensoria name
function extrairNumeroDefensoria(nome: string): number | null {
  const match = nome.match(/(\d+)[ªº]?\s/);
  return match ? parseInt(match[1], 10) : null;
}

// Helper function to sort defensorias (same logic as in tLotacoesPorComarca.tsx)
function ordenarDefensorias(defensorias: IReportLocalLotacao[]): IReportLocalLotacao[] {
  return [...defensorias].sort((a, b) => {
    const numeroA = extrairNumeroDefensoria(a.nome);
    const numeroB = extrairNumeroDefensoria(b.nome);

    if (numeroA !== null && numeroB !== null) {
      return numeroA - numeroB;
    }

    if (numeroA !== null) return -1;
    if (numeroB !== null) return 1;

    return a.nome.localeCompare(b.nome);
  });
}

// Helper function to verify if a defensoria has selected defenders
function hasSelectedDefenders(relatorio: IFullReport, defensoria: IReportLocalLotacao, selectedDefensores: string[]): boolean {
  const lotacoes = relatorio.lotacoes.filter(
    (a) => a.defensoria_id === defensoria.id && a.defensor_id && isAtivoHoje(a.data_inicial, a.data_final),
  );

  if (selectedDefensores.length === 0) return true;

  return lotacoes.some((lotacao) => {
    const defensor = relatorio.pessoas.find(
      (a) => a.id === lotacao.defensor_id,
    );
    if (!defensor) return false;
    const defensorNome = `${defensor.titulo?.trim() || ''} ${defensor.nome?.trim()}`.trim();
    return selectedDefensores.includes(defensorNome);
  });
}

// Helper function to get defenders
export function getDefensores(relatorio: IFullReport, defensoriaId: number, selectedDefensores: string[], exibirEventos: boolean = false) {
  const lotacoes = relatorio.lotacoes
    .filter(
      (l) =>
        l.defensoria_id === defensoriaId &&
        l.defensor_id &&
        isAtivoHoje(l.data_inicial, l.data_final),
    );

  if (!lotacoes.length) return [<Text key={`vago-${defensoriaId}`}>(VAGO)</Text>];

  const titulares = lotacoes.filter((l) => l.tipo === 1); // Assuming 1 is TipoLotacao.Titularidade
  const naoTitulares = lotacoes.filter((l) => l.tipo !== 1);

  const elementos: any[] = [];

  [titulares, naoTitulares].forEach((group) => {
    group.forEach((lot, index) => {
      const defensor = relatorio.pessoas.find((p) => p.id === lot.defensor_id);
      if (!defensor) return;

      const defensorNome = `${defensor.titulo || ''} ${defensor.nome}`.trim();
      if (selectedDefensores.length > 0 && !selectedDefensores.includes(defensorNome)) return;

      const anotacoes = [lot.anotacao1, lot.anotacao2].filter(Boolean).join(' - ');

      elementos.push(
        <View key={`L${lot.id}-${index}`} wrap={false}>
          <Text style={{ color: lot.ativo ? 'black' : 'gray' }}>
            {defensorNome}
          </Text>
          {anotacoes && (
            <Text style={{ fontStyle: 'italic' }}>{anotacoes}</Text>
          )}
          {exibirEventos && lot.ativo && defensor.abonos && (() => {
            const eventos = getEventos(relatorio, defensor.id, defensoriaId);
            return eventos ? (
              <Text style={{ color: 'red', fontStyle: 'italic' }}>{eventos}</Text>
            ) : null;
          })()}
        </View>
      );
    });
  });

  return elementos.length ? elementos : [<Text key={`vago-${defensoriaId}`}>(VAGO)</Text>];
}

// Helper function to get interns
export function getEstagiarios(relatorio: IFullReport, defensoriaId: number) {
  const lotacoes = relatorio.lotacoes
    .filter((l) => l.defensoria_id === defensoriaId && l.estagio_id && isAtivoHoje(l.data_inicial, l.data_final));

  if (!lotacoes.length) return 'VAGO';

  const estagiarios = lotacoes
    .map((l) => {
      const estagiario = relatorio.pessoas.find((p) =>
        p.estagios?.some((e) => e.id === l.estagio_id),
      );
      return estagiario ? `${estagiario.titulo || ''} ${estagiario.nome}`.trim() : null;
    })
    .filter(Boolean);

  return estagiarios.length ? estagiarios.join('\n') : 'VAGO';
}

// Helper function to get substitutions
export function getSubstituicoes(relatorio: IFullReport, substituta1_id?: number, substituta2_id?: number) {
  const nome1 = relatorio.comarcas
    .flatMap((c) => c.locais)
    .find((l) => l.id === substituta1_id)?.nome || '(VAGO)';
  const nome2 = relatorio.comarcas
    .flatMap((c) => c.locais)
    .find((l) => l.id === substituta2_id)?.nome || '(VAGO)';

  return (
    <View>
      <Text style={{ fontWeight: 'bold' }}>1° SUBSTITUTO</Text>
      <Text>{nome1}</Text>
      <Text style={{ fontWeight: 'bold' }}>2° SUBSTITUTO</Text>
      <Text>{nome2}</Text>
    </View>
  );
}

// Main component
export default function ComarcaPDF(
  {
    relatorio,
    exibirEstagiarios,
    exibirEventos,
    agruparAtuacao,
    agruparPorPredio,
    agruparPorDiretoria,
    selectedComarcas,
    selectedDefensorias,
    selectedDefensores,
  }: IProps) {

  if (!relatorio || !relatorio.comarcas) {
    return (
      <Document>
        <Page size="A4" style={stylesDefensorComarca.page} orientation="landscape">
          <Text style={stylesDefensorComarca.title}>Carregando...</Text>
        </Page>
      </Document>
    );
  }

  let titulo1 = 0;
  // Cálculo do tamanho das colunas: quando agruparPorPredio está ativo, não exibe coluna de Endereço/Telefone
  // Colunas sempre presentes: Órgão de Atuação, Defensor(a), Substituição = 3
  // Colunas condicionais: Endereço/Telefone (se !agruparPorPredio) + Estagiários (se exibirEstagiarios)
  const numColunas = 3 + (!agruparPorPredio ? 1 : 0) + (exibirEstagiarios ? 1 : 0);
  const colBase = 100 / numColunas;

  return (
    <Document>
      {relatorio.comarcas
        .filter((comarca) => {
          const comarcaNome = `${comarca.prefixo || ''} ${comarca.nome}`.trim();
          if (selectedComarcas.length > 0) {
            return selectedComarcas.includes(comarcaNome);
          }
          return comarca.locais.some(
            (defensoria) =>
              (selectedDefensorias.length === 0 ||
                selectedDefensorias.includes(defensoria.nome)) &&
              hasSelectedDefenders(relatorio, defensoria, selectedDefensores),
          );
        })
        .map((comarca: IReportComarca) => {
          titulo1++;
          let titulo2 = 0;

          const atuacoesDessaComarca = agruparAtuacao
            ? relatorio.atuacoes
              .filter((a) => comarca.locais.some((b) => b.atuacao_id === a.id))
              .sort((a, b) => (a.ordem || 0) - (b.ordem || 0))
            : [{ id: 0, nome: '', ordem: 0 }];

          return (
            <Page key={comarca.id} size="A4" style={stylesDefensorComarca.page} orientation="landscape" wrap>
              <Text style={stylesDefensorComarca.title}>
                LOTAÇÃO DOS DEFENSORES PÚBLICOS COM OS RESPECTIVOS ENDEREÇOS ONDE ATUAM
              </Text>
              <Text style={stylesDefensorComarca.updated}>
                Atualizado em {DataAmigavel(relatorio.editadoem)}
              </Text>
              <Text style={stylesDefensorComarca.comarcaTitle}>
                {`${titulo1}. Da Comarca de ${comarca.prefixo || ''} ${comarca.nome}`.trim()}
              </Text>

              {atuacoesDessaComarca.map((atuacao) => {
                titulo2++;
                let titulo3 = 0;

                const defensoriasFiltradas = comarca.locais
                  .filter((d) => !agruparAtuacao || d.atuacao_id === atuacao.id)
                  .filter((d) => selectedDefensorias.length === 0 || selectedDefensorias.includes(d.nome))
                  .filter((d) => hasSelectedDefenders(relatorio, d, selectedDefensores));

                const gruposDefensorias = montarGruposDefensorias(
                  defensoriasFiltradas,
                  agruparPorDiretoria,
                  agruparPorPredio,
                  ordenarDefensorias,
                );

                // Função para renderizar a tabela de defensorias
                const renderizarTabela = (defensoriasParaRenderizar: IReportLocalLotacao[]) => {
                  const paginas = paginarLinhas(defensoriasParaRenderizar);
                  
                  return (
                    <View style={stylesDefensorComarca.table}>
                      <View style={stylesDefensorComarca.rowEven}>
                        <Text style={[stylesDefensorComarca.cellHeader, { width: `${colBase}%` }]}>
                          Órgão de Atuação
                        </Text>
                        {!agruparPorPredio && (
                          <Text style={[stylesDefensorComarca.cellHeader, { width: `${colBase}%` }]}>
                            Endereço/Telefone
                          </Text>
                        )}
                        <Text style={[stylesDefensorComarca.cellHeader, { width: `${colBase}%` }]}>
                          Defensor(a)
                        </Text>
                        {exibirEstagiarios && (
                          <Text style={[stylesDefensorComarca.cellHeader, { width: `${colBase}%` }]}>
                            Estagiário(s)
                          </Text>
                        )}
                        <Text style={[stylesDefensorComarca.cellHeader, { width: `${colBase}%` }]}>
                          Substituição
                        </Text>
                      </View>

                      {paginas.map((pagina, paginaIndex) => (
                        <View key={`pagina-${paginaIndex}`} break={paginaIndex > 0}>
                          {pagina.map((defensoria, index) => {
                            const predio = comarca.predios.find((p) => p.id === defensoria.predio_id);
                            const vara = relatorio.varas.find((v) => v.id === defensoria.vara_id);
                            const atuacao = (
                              <Text>
                                <Text style={stylesDefensorComarca.bold}>{defensoria.nome}</Text>
                                {vara && (
                                  <>
                                    {'\n'}
                                    <Text style={{ fontStyle: 'italic' }}>({vara.nome})</Text>
                                  </>
                                )}
                              </Text>
                            );

                            const endereco = [
                              predio?.nome,
                              predio?.endereco?.logradouro && `${predio.endereco.logradouro}, ${predio.endereco.bairro}, ${predio.endereco.cidade}`,
                              defensoria.anotacao && <Text style={{ fontWeight: 'bold' }}>{`*${defensoria.anotacao}*`}</Text>,
                              predio?.telefoneprincipal && `Fórum: ${predio.telefoneprincipal}`,
                              predio?.telefonesecundario && predio.telefonesecundario !== "0" && `Fórum Secundário: ${predio.telefonesecundario}`,
                              defensoria.telefone && `Defensoria: ${defensoria.telefone}`,
                            ].filter(Boolean);

                            return (
                              <View key={defensoria.id} style={index % 2 === 0 ? stylesDefensorComarca.rowOdd : stylesDefensorComarca.rowEven} wrap={false}>
                                <View style={[stylesDefensorComarca.cell, { width: `${colBase}%` }]}>
                                  {atuacao}
                                </View>
                                {!agruparPorPredio && (
                                  <View style={[stylesDefensorComarca.cell, { width: `${colBase}%` }]}>
                                    {endereco.map((item, i) => (
                                      <Text key={i}>{item}</Text>
                                    ))}
                                  </View>
                                )}
                                <View style={[stylesDefensorComarca.cell, { width: `${colBase}%` }]}>
                                  {getDefensores(relatorio, defensoria.id, selectedDefensores, exibirEventos)}
                                </View>
                                {exibirEstagiarios && (
                                  <Text style={[stylesDefensorComarca.cell, { width: `${colBase}%` }]}>
                                    {getEstagiarios(relatorio, defensoria.id)}
                                  </Text>
                                )}
                                <View style={[stylesDefensorComarca.cell, { width: `${colBase}%` }]}>
                                  {getSubstituicoes(
                                    relatorio,
                                    defensoria.substituta1_id,
                                    defensoria.substituta2_id
                                  )}
                                </View>
                              </View>
                            );
                          })}
                        </View>
                      ))}
                    </View>
                  );
                };

                return (
                  <View key={atuacao.id} wrap>
                    {agruparAtuacao && atuacao.nome && (
                      <Text style={stylesDefensorComarca.atuacaoTitle}>
                        {`${titulo1}.${titulo2}. Das Defensorias Públicas - ${atuacao.nome}`}
                      </Text>
                    )}

                    {defensoriasFiltradas.length > 0 && gruposDefensorias.map((grupo) => {
                      titulo3++;
                      const predio = grupo.predioId != null
                        ? comarca.predios.find((p) => p.id === grupo.predioId)
                        : undefined;

                      return (
                        <View key={`grupo-${grupo.diretoriaId ?? 'x'}-${grupo.predioId ?? 'x'}`} wrap>
                          {agruparPorDiretoria && grupo.diretoriaNome && (
                            <Text style={stylesDefensorComarca.diretoriaTitle}>
                              {`${titulo1}.${titulo2}.${titulo3}. Diretoria Regional - ${grupo.diretoriaNome}`}
                            </Text>
                          )}
                          {agruparPorPredio && predio && (
                            <Text style={stylesDefensorComarca.predioTitle}>
                              {`${titulo1}.${titulo2}.${titulo3}. ${predio.nome} - ${predio.endereco?.logradouro || ''} ${predio.telefoneprincipal || ''}`}
                            </Text>
                          )}
                          {renderizarTabela(grupo.defensorias)}
                        </View>
                      );
                    })}
                  </View>
                );
              })}
              <Text
                style={stylesDefensorComarca.footer}
                render={({ pageNumber, totalPages }) =>
                  `Página ${pageNumber} de ${totalPages} | Emitido em ${DataAmigavel(new Date())} às ${new Date().toLocaleTimeString('pt-BR', {
                    hour: '2-digit',
                    minute: '2-digit',
                  })}`
                }
                fixed
              />
            </Page>
          );
        })}
    </Document>
  );
}